import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import {
  createExerciseTrackerInstruction,
  deleteExerciseTrackerInstruction,
  ExerciseTrackerInstruction,
  ExerciseTrackerInstructionPayload,
  getExerciseTrackerInstructions,
  updateExerciseTrackerInstruction
} from '../lib/api/exerciseTrackerInstructions'
import { Exercise, getExercises } from '../lib/api/exercises'
import { buildHeaderIndex, downloadCsv, makeGetValue, normalizeText, parseCsvText } from '../utils/csv'

const initialForm: ExerciseTrackerInstructionPayload = {
  key: '',
  title: '',
  instructionsMarkdown: ''
}

const INSTRUCTION_CSV_COLUMNS = [
  'Id',
  'Key',
  'Title',
  'InstructionsMarkdown',
  'CreatedAt',
  'UpdatedAt'
] as const

type InstructionCsvColumnKey = (typeof INSTRUCTION_CSV_COLUMNS)[number]
type InstructionImportAction = 'create' | 'update'

type ParsedInstructionImportRow = {
  action: InstructionImportAction
  payload: ExerciseTrackerInstructionPayload
  targetId?: string
}

const INSTRUCTION_HEADER_ALIASES: Record<InstructionCsvColumnKey, string[]> = {
  Id: ['id'],
  Key: ['key'],
  Title: ['title', 'name'],
  InstructionsMarkdown: ['instructionsmarkdown', 'instructions', 'markdown', 'content', 'body'],
  CreatedAt: ['createdat'],
  UpdatedAt: ['updatedat']
}

const INSTRUCTION_CSV_FALLBACK_INDEX: Record<InstructionCsvColumnKey, number> = {
  Id: 0,
  Key: 1,
  Title: 2,
  InstructionsMarkdown: 3,
  CreatedAt: 4,
  UpdatedAt: 5
}

const normalizeInstructionKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '')

const formatDate = (value?: string | null) => {
  if (!value) {
    return 'Recently updated'
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(value))
  } catch (error) {
    console.warn('Failed to format date', error)
    return value
  }
}

const buildMarkdownPreview = (markdown: string) => {
  const lines = markdown.trim().split('\n')
  if (lines.length <= 10) {
    return markdown.trim()
  }

  return `${lines.slice(0, 10).join('\n')}\n\n...`
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mb-3 mt-1 text-xl font-semibold text-foreground first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 text-lg font-semibold text-foreground first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-4 text-base font-semibold text-foreground first:mt-0">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mb-3 leading-6 text-foreground/90 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-3 ml-5 list-disc space-y-1 text-foreground/90">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 ml-5 list-decimal space-y-1 text-foreground/90">{children}</ol>
  ),
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  )
}

export const ExerciseTrackerInstructionsPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const [instructions, setInstructions] = useState<ExerciseTrackerInstruction[]>([])
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [form, setForm] = useState<ExerciseTrackerInstructionPayload>(initialForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isCsvImporting, setIsCsvImporting] = useState(false)
  const [isCsvExporting, setIsCsvExporting] = useState(false)
  const csvInputRef = useRef<HTMLInputElement | null>(null)

  const role = (user?.role ?? 'Guest').toString()
  const isEditor = role === 'Admin' || role === 'Editor'

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [instructionData, exerciseData] = await Promise.all([
        getExerciseTrackerInstructions(),
        getExercises()
      ])

      setInstructions(instructionData)
      setExercises(exerciseData)
    } catch (error) {
      console.error('Failed to load exercise tracker instructions', error)
      addToast({
        title: 'Could not load instructions',
        description: 'Please refresh or try again later.'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isEditor) {
      setLoading(false)
      return
    }

    void loadData()
  }, [isEditor, loadData])

  const usageByInstructionKey = useMemo(() => {
    return exercises.reduce<Record<string, { count: number; codes: string[] }>>((acc, exercise) => {
      const key = exercise.exerciseTrackerInstructionKey?.trim().toLowerCase()
      if (!key) {
        return acc
      }

      if (!acc[key]) {
        acc[key] = { count: 0, codes: [] }
      }

      acc[key].count += 1
      acc[key].codes.push(exercise.code)
      return acc
    }, {})
  }, [exercises])

  const filteredInstructions = useMemo(() => {
    const query = normalizeText(searchQuery)
    if (!query) {
      return instructions
    }

    return instructions.filter((instruction) => {
      return [instruction.key, instruction.title, instruction.instructionsMarkdown]
        .some((value) => normalizeText(value).includes(query))
    })
  }, [instructions, searchQuery])

  const resetForm = () => {
    setForm(initialForm)
    setEditingId(null)
    setFormError(null)
    setIsEditModalOpen(false)
  }

  const openAddDialog = () => {
    resetForm()
    setIsEditModalOpen(true)
  }

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const key = normalizeInstructionKey(form.key)
      const title = form.title.trim()
      const instructionsMarkdown = form.instructionsMarkdown.trim()

      if (!key || !title || !instructionsMarkdown) {
        setFormError('Key, title, and markdown instructions are required.')
        return
      }

      setFormError(null)
      setSaving(true)

      try {
        const payload: ExerciseTrackerInstructionPayload = {
          key,
          title,
          instructionsMarkdown
        }

        if (editingId) {
          await updateExerciseTrackerInstruction(editingId, payload)
          addToast({ title: 'Instruction updated', description: 'Changes saved successfully.' })
        } else {
          await createExerciseTrackerInstruction(payload)
          addToast({ title: 'Instruction added', description: 'New tracker instruction created.' })
        }

        await loadData()
        resetForm()
      } catch (error) {
        console.error('Failed to save exercise tracker instruction', error)
        const message =
          error instanceof Error && error.message.includes('409')
            ? 'An instruction with this key already exists.'
            : 'Could not save this instruction. Please try again.'

        addToast({
          title: 'Save failed',
          description: message
        })
      } finally {
        setSaving(false)
      }
    },
    [addToast, editingId, form, loadData]
  )

  const handleEdit = (instruction: ExerciseTrackerInstruction) => {
    setEditingId(instruction.id)
    setForm({
      key: instruction.key,
      title: instruction.title,
      instructionsMarkdown: instruction.instructionsMarkdown
    })
    setFormError(null)
    setIsEditModalOpen(true)
  }

  const handleDelete = async (instruction: ExerciseTrackerInstruction) => {
    const usage = usageByInstructionKey[instruction.key]
    if (usage?.count) {
      addToast({
        title: 'Instruction is still in use',
        description: `Remove it from ${usage.count} linked exercise${usage.count === 1 ? '' : 's'} first.`
      })
      return
    }

    const confirmed = window.confirm(`Delete exercise tracker instruction "${instruction.title}"?`)
    if (!confirmed) {
      return
    }

    setDeletingId(instruction.id)
    try {
      await deleteExerciseTrackerInstruction(instruction.id)
      await loadData()
      addToast({ title: 'Instruction removed', description: 'It has been deleted from the library.' })
      if (editingId === instruction.id) {
        resetForm()
      }
    } catch (error) {
      console.error('Failed to delete exercise tracker instruction', error)
      addToast({
        title: 'Delete failed',
        description: 'Unable to delete this tracker instruction right now.'
      })
    } finally {
      setDeletingId(null)
    }
  }

  const handleExportCsv = async () => {
    setIsCsvExporting(true)
    try {
      const rows = instructions.map((instruction) => [
        instruction.id,
        instruction.key,
        instruction.title,
        instruction.instructionsMarkdown,
        instruction.createdAt,
        instruction.updatedAt ?? ''
      ])

      const timestamp = new Date().toISOString().slice(0, 10)
      downloadCsv([...INSTRUCTION_CSV_COLUMNS], rows, `hikestrong-exercise-tracker-instructions-${timestamp}.csv`)
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} instructions.` })
    } catch (error) {
      console.error('Failed to export exercise tracker instructions CSV', error)
      addToast({
        title: 'Export failed',
        description: 'Could not export the tracker instructions CSV file.'
      })
    } finally {
      setIsCsvExporting(false)
    }
  }

  const parseInstructionCsvRows = async (file: File) => {
    const text = await file.text()
    const rows = parseCsvText(text)

    if (rows.length === 0) {
      throw new Error('CSV file is empty.')
    }

    const header = rows[0].map((value) => value.trim())
    const hasHeader =
      header.some((column) => normalizeText(column) === 'key') &&
      header.some((column) => normalizeText(column) === 'title')

    const dataRows = hasHeader ? rows.slice(1) : rows
    if (dataRows.length === 0) {
      throw new Error('CSV appears to contain only headers.')
    }

    const headerIndex = hasHeader ? buildHeaderIndex(header, INSTRUCTION_HEADER_ALIASES) : null
    const getValue = makeGetValue(headerIndex, INSTRUCTION_CSV_FALLBACK_INDEX)
    const existingByKey = new Map(
      instructions.map((instruction) => [normalizeInstructionKey(instruction.key), instruction])
    )

    const rowsToImport: ParsedInstructionImportRow[] = []

    dataRows.forEach((row, index) => {
      const lineNumber = hasHeader ? index + 2 : index + 1
      const key = normalizeInstructionKey(getValue(row, 'Key'))
      const title = getValue(row, 'Title').trim()
      const instructionsMarkdown = getValue(row, 'InstructionsMarkdown')

      if (!key || !title || !instructionsMarkdown.trim()) {
        throw new Error(`Row ${lineNumber}: key, title, and instructions markdown are required.`)
      }

      const existingMatch = existingByKey.get(key)

      rowsToImport.push({
        action: existingMatch ? 'update' : 'create',
        targetId: existingMatch?.id,
        payload: {
          key,
          title,
          instructionsMarkdown
        }
      })
    })

    return rowsToImport
  }

  const handleImportCsv = async (file: File) => {
    setIsCsvImporting(true)
    try {
      const rowsToImport = await parseInstructionCsvRows(file)
      let created = 0
      let updated = 0
      const errors: string[] = []

      for (const row of rowsToImport) {
        try {
          if (row.action === 'update' && row.targetId) {
            await updateExerciseTrackerInstruction(row.targetId, row.payload)
            updated += 1
          } else {
            await createExerciseTrackerInstruction(row.payload)
            created += 1
          }
        } catch (error) {
          console.error('Failed to import exercise tracker instruction', row.payload.key, error)
          errors.push(row.payload.key)
        }
      }

      if (created > 0 || updated > 0) {
        await loadData()
      }

      if (errors.length > 0) {
        addToast({
          title: 'Import completed with errors',
          description: `Imported ${created + updated} instructions with ${errors.length} failed.`
        })
        return
      }

      const details = []
      if (created > 0) {
        details.push(`${created} created`)
      }
      if (updated > 0) {
        details.push(`${updated} updated`)
      }

      addToast({
        title: 'Import complete',
        description: details.length > 0 ? `Imported ${details.join(' and ')}.` : 'No instructions imported.'
      })
    } catch (error) {
      console.error('Failed to import exercise tracker instructions CSV', error)
      addToast({
        title: 'Import failed',
        description: error instanceof Error ? error.message : 'Could not parse or import the CSV file.'
      })
    } finally {
      setIsCsvImporting(false)
    }
  }

  if (!isEditor) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Limited Access
          </CardTitle>
          <CardDescription>
            Exercise tracker instructions can only be managed by Admin or Editor accounts.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  const instructionEditorCard = (
    <Card className="w-full shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-primary" />
              {editingId ? 'Edit tracker instruction' : 'Add tracker instruction'}
            </CardTitle>
            <CardDescription>
              Store reusable markdown instructions that exercises can reference by key.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={resetForm}
            disabled={saving}
            title={editingId ? 'Close edit modal' : 'Close add instruction modal'}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="instruction-key">
                Key
              </label>
              <Input
                id="instruction-key"
                value={form.key}
                onChange={(event) => setForm((prev) => ({ ...prev, key: event.target.value }))}
                placeholder="stairs_tracker"
                disabled={saving}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="instruction-title">
                Title
              </label>
              <Input
                id="instruction-title"
                value={form.title}
                onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Stairs Tracker Instructions"
                disabled={saving}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="instruction-markdown">
              Markdown Instructions
            </label>
            <Textarea
              id="instruction-markdown"
              value={form.instructionsMarkdown}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, instructionsMarkdown: event.target.value }))
              }
              rows={16}
              placeholder={'# Key Tips\n\n- Warm up first\n- Keep your whole foot on each step'}
              disabled={saving}
              className="min-h-[320px] font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Preview</p>
            <div className="rounded-md border border-border bg-muted/20 px-4 py-3">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {form.instructionsMarkdown.trim() || '*Nothing to preview yet.*'}
              </ReactMarkdown>
            </div>
          </div>

          {formError ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {formError}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {editingId ? 'Save changes' : 'Add instruction'}
            </Button>
            {editingId ? (
              <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                Cancel edit
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-4">
      <Card className="w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 shadow-xl">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-emerald-500/20 p-3 ring-1 ring-emerald-500/30">
              <FileText className="h-6 w-6 text-emerald-200" />
            </div>
            <div>
              <CardTitle className="text-lg">Exercise Tracker Instructions</CardTitle>
              <CardDescription className="text-muted-foreground">
                Reusable markdown instructions that multiple exercises can share.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div className="flex flex-wrap gap-2">
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) {
                  void handleImportCsv(file)
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (csvInputRef.current) {
                  csvInputRef.current.value = ''
                  csvInputRef.current.click()
                }
              }}
              disabled={loading || isCsvImporting}
              className="gap-2"
            >
              {isCsvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleExportCsv()}
              disabled={loading || isCsvExporting}
              className="gap-2"
            >
              {isCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </Button>
            <Button variant="secondary" size="sm" onClick={openAddDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Add instruction
            </Button>
          </div>

          <div className="relative max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by key, title, or markdown"
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {isEditModalOpen ? (
        <Dialog open={isEditModalOpen} onOpenChange={(isOpen) => !isOpen && resetForm()}>
          <DialogContent className="max-w-4xl">
            <DialogTitle className="sr-only">
              {editingId ? 'Edit tracker instruction' : 'Add tracker instruction'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Use this form to update reusable exercise tracker instructions.
            </DialogDescription>
            {instructionEditorCard}
          </DialogContent>
        </Dialog>
      ) : null}

      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Instruction Library</h2>
          <p className="text-sm text-muted-foreground">
            {filteredInstructions.length} instruction{filteredInstructions.length === 1 ? '' : 's'} available.
          </p>
        </div>

        {loading ? (
          <Card className="flex items-center gap-3 px-6 py-5">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading exercise tracker instructions…</p>
          </Card>
        ) : filteredInstructions.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No tracker instructions yet"
            description="Add the first markdown instruction or import your library from CSV."
          >
            <Button onClick={openAddDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Add instruction
            </Button>
          </EmptyState>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {filteredInstructions.map((instruction) => {
              const usage = usageByInstructionKey[instruction.key]
              const preview = buildMarkdownPreview(instruction.instructionsMarkdown)

              return (
                <Card key={instruction.id} className="flex h-full flex-col shadow-sm">
                  <CardHeader className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <FileText className="h-5 w-5 text-primary" />
                          {instruction.title}
                        </CardTitle>
                        <p className="text-xs font-mono text-muted-foreground">{instruction.key}</p>
                      </div>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          usage?.count ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {usage?.count ?? 0} linked exercise{usage?.count === 1 ? '' : 's'}
                      </span>
                    </div>
                    <CardDescription>
                      Updated {formatDate(instruction.updatedAt ?? instruction.createdAt)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col gap-4">
                    <div className="rounded-md border border-border bg-muted/20 p-3">
                      <pre className="whitespace-pre-wrap break-words text-sm leading-6 text-foreground/90">
                        {preview}
                      </pre>
                    </div>

                    {usage?.codes?.length ? (
                      <div className="text-xs text-muted-foreground">
                        Linked exercises: {usage.codes.slice(0, 6).join(', ')}
                        {usage.codes.length > 6 ? ` +${usage.codes.length - 6} more` : ''}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        This instruction is not linked to any exercises yet.
                      </div>
                    )}

                    <div className="mt-auto flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(instruction)}
                        disabled={saving || deletingId === instruction.id}
                        className="gap-1"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => void handleDelete(instruction)}
                        disabled={saving || deletingId === instruction.id}
                      >
                        {deletingId === instruction.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
